import { useMemo } from "react";
import { useLocalStorage } from "./local-storage";

export type LessonData = {
    title: string;
    content: { text: string; indent: number }[];
}[];

export type PsalmCycleSetting = "bcp" | "acna" | "cw";

export type BibleTranslationSetting =
    | "ESV"
    | "NRSV"
    | "KJV"
    | "RSV"
    | "NASB"
    | "RSV-CE";

export type ReadingsSetting =
    | "acna-morning-short"
    | "acna-morning-long"
    | "acna-evening-short"
    | "acna-evening-long"
    | "1871-morning"
    | "1871-evening"
    | "cw-morning"
    | "cw-evening"
    | "1922-morning"
    | "1922-evening"
    | "1961-morning"
    | "1961-evening"
    | "1662-morning"
    | "1662-evening"
    | "ord-morning"
    | "ord-evening";

export const getEveningReadingsFromMorningReadings = (
    morningReadings: ReadingsSetting
): ReadingsSetting => {
    const result: any = morningReadings.replace("morning", "evening");

    return result;
};

export interface Settings {
    bibleTranslation: BibleTranslationSetting;

    psalmsBold: "all" | "alternate" | "none";

    invitatoryAntiphons: "always" | "seasons" | "never";

    antiphonTones: "solemn" | "standard";

    psalmCycle: PsalmCycleSetting;

    teDeum: "always" | "feasts" | "never";

    mattinsReadings: ReadingsSetting;

    monarch: "yes" | "no";

    salutation: "o lord hear our prayer" | "the lord be with you";

    psalmsAtCompline: "compline" | "compline-short" | "same as evensong";

    secondReadingToCompline: "yes" | "no";

    letUsBlessMattinsAndEvensong: "omit" | "after collects" | "before grace";

    transpositions: {
        [key: string]: number;
    };

    hymnPosition: "anthem" | "before psalms" | "before canticle" | "omit";

    gospelCanticleTones: "standard" | "solemn";

    absolution: "omit" | "bcp absolution" | "pardon and peace collect";

    lordsPrayerAfterConfession: "omit" | "said" | "sung";

    lordsPrayer: "bcp" | "standard";

    adventAndLentCollects: "repeat" | "dont repeat";

    paschaNostrum: "octave" | "day" | "no";

    canticleAntiphonsExperimental: "none" | "sarum";

    canticleTonesExperimental: "simplified" | "sarum";

    experimentalChantRendering: "off" | "on";

    tuning: "enabled" | "disabled";

    transferEpiphany: "yes" | "no";

    transferAscension: "yes" | "no";

    transferCorpusChristi: "yes" | "no";

    oneDayAheadPsalter: "true" | "false";

    marianAntiphonLanguage: "Latin" | "English";
}

export const DEFAULT_SETTINGS: Settings = {
    bibleTranslation: "ESV",
    psalmsBold: "none",
    invitatoryAntiphons: "always",
    antiphonTones: "solemn",
    psalmCycle: "bcp",
    teDeum: "always",
    mattinsReadings: "1961-morning",
    monarch: "yes",
    salutation: "the lord be with you",
    psalmsAtCompline: "compline-short",
    secondReadingToCompline: "no",
    letUsBlessMattinsAndEvensong: "after collects",
    transpositions: {
        "1": 0,
        "2": 0,
        "3": 0,
        "4": 0,
        "5": 0,
        "6": 0,
        "7": 0,
        "8": 0,
        T: 0,
    },
    hymnPosition: "before canticle",
    gospelCanticleTones: "standard",
    absolution: "pardon and peace collect",
    lordsPrayerAfterConfession: "omit",
    lordsPrayer: "standard",
    adventAndLentCollects: "repeat",
    paschaNostrum: "octave",
    canticleAntiphonsExperimental: "none",
    canticleTonesExperimental: "simplified",
    tuning: "disabled",
    experimentalChantRendering: "off",
    transferEpiphany: "no",
    transferAscension: "no",
    transferCorpusChristi: "no",
    oneDayAheadPsalter: "false",
    marianAntiphonLanguage: "Latin",
};

export const useSettings = (): {
    settings: Settings;
    setSetting: (key: string, value: any) => void;
} => {
    const [settings, setSettings] = useLocalStorage<Partial<Settings>>(
        "app-settings",
        DEFAULT_SETTINGS
    );

    return useMemo(() => {
        return {
            settings: { ...DEFAULT_SETTINGS, ...settings },
            setSetting: (key: string, value: string) => {
                setSettings({ ...settings, [key]: value });
            },
        };
    }, [settings, setSettings]);
};
